import { FormEvent, useState } from 'react';
import { api, Backup, BackupInfo } from '../../api';
import { Alert, Loading, StatusTag, useAction, useLoad } from '../../Components/ui';
import type { TabProps } from './ControlPanel';

/** GET /services/:id/backups (B2B API). Creating and restoring backups is the backupManagement feature. */
export default function BackupsTab({ base, detail }: TabProps) {
    const canManage = detail.capabilities.backupManagement;
    const { data: info, error, loading, reload } = useLoad(
        () => api.get<{ backups: BackupInfo }>(`${base}/backups`).then(r => r.data.backups), [base]);
    const [label, setLabel] = useState('');
    const [message, setMessage] = useState<string | null>(null);
    const action = useAction();

    const create = (e: FormEvent) => {
        e.preventDefault();
        void action.run(async () => {
            await api.post(`${base}/backups`, { label: label || undefined });
            setLabel('');
            setMessage('Backup created.');
            await reload();
        });
    };

    const restore = (b: Backup) => {
        if (!window.confirm(`Restore the backup from ${new Date(b.createdAt).toLocaleString('sv-SE')}? Current files and databases will be replaced.`)) return;
        void action.run(async () => {
            await api.post(`${base}/backups/${encodeURIComponent(b.id)}/restore`);
            setMessage('Restore started. It can take a few minutes to complete.');
        });
    };

    return (
        <div className="panel-card">
            <h3>Backups</h3>
            {message && <Alert type="success">{message}</Alert>}
            {action.error && <Alert type="error">{action.error}</Alert>}
            {error && <Alert type="error">{error}</Alert>}
            {info && (
                <div className="kv-grid" style={{ marginBottom: '1.25rem' }}>
                    <div><div className="kv-label">Schedule</div><div className="kv-value">{info.frequency ? `Automatic, ${info.frequency}` : 'Manual only'}</div></div>
                    <div><div className="kv-label">Kept for</div><div className="kv-value">{info.retentionDays ? `${info.retentionDays} days` : '—'}</div></div>
                    <div><div className="kv-label">Status</div><div className="kv-value">{info.status ? <StatusTag status={info.status === 'healthy' ? 'active' : info.status} /> : '—'}</div></div>
                </div>
            )}
            {canManage ? (
                <form className="fi-form" onSubmit={create} style={{ marginBottom: '1.25rem' }}>
                    <div className="inline-fields">
                        <label style={{ gridColumn: 'span 2' }}>
                            Label (optional)
                            <input value={label} onChange={e => setLabel(e.target.value)} maxLength={100} placeholder="Before update" />
                        </label>
                        <button className="btn-submit inline" disabled={action.busy}>Create backup now</button>
                    </div>
                </form>
            ) : (
                <p className="card-text" style={{ marginBottom: '1rem' }}>Backups are taken automatically by CarbonEdge. Contact us if you need a restore.</p>
            )}
            {loading && !info ? <Loading /> : (
                <div className="table-wrap">
                    <table className="data-table">
                        <thead><tr><th>Created</th><th>Label</th><th>Type</th><th>Size</th><th>Status</th>{canManage && <th />}</tr></thead>
                        <tbody>
                            {info?.backups.map(b => (
                                <tr key={b.id}>
                                    <td>{new Date(b.createdAt).toLocaleString('sv-SE')}</td>
                                    <td>{b.label ?? '—'}</td>
                                    <td>{b.kind}</td>
                                    <td>{b.sizeMb === null ? '—' : `${b.sizeMb} MB`}</td>
                                    <td><StatusTag status={b.status} /></td>
                                    {canManage && (
                                        <td className="actions">
                                            <button className="btn-outline small" disabled={action.busy || b.status !== 'completed'} onClick={() => restore(b)}>Restore</button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                            {info?.backups.length === 0 && <tr><td colSpan={6} className="empty-state">No backups yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
